import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IndianRupee,
  TrendingUp,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  Building2,
  Loader2,
  Award,
  Users,
  Settings,
  Filter,
  Eye,
  Calculator,
  X,
  ChevronDown,
  ChevronRight,
  Target,
  EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { incentivesAPI, usersAPI, dealsAPI, demosAPI } from '../../services/api';
import UpcomingPayouts from '../../components/incentives/UpcomingPayouts';
import UpcomingInvoices from '../../components/admin/UpcomingInvoices';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';

import { toast } from '../../hooks/use-toast';

const getStatusColor = (status) => {
  const colors = {
    PENDING: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
    APPROVED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
    PAYABLE: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
    PAID: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
    INVOICED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
    EXPECTED: 'bg-slate-100 text-slate-700 dark:bg-slate-900/50 dark:text-slate-400',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

/* eslint-disable react/no-unstable-nested-components */

const IncentivesPage = () => {
  const { user: currentUser } = useAuth();
  const { canSeeIncentives, settingsLoading, formatMoney } = usePrivacy();
  const navigate = useNavigate();
  const [incentives, setIncentives] = useState([]);
  const [users, setUsers] = useState([]);
  const [teamConfigs, setTeamConfigs] = useState([]);
  const [teamSqdSlabs, setTeamSqdSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('team-incentives');
  
  // Company Receivables (Admin Only)
  const [companyReceivables, setCompanyReceivables] = useState([]);
  const [receivableStats, setReceivableStats] = useState({});
  const [revenueTab, setRevenueTab] = useState('all'); // 'all', 'sqd', 'conversion'
  const [expandedRevenueId, setExpandedRevenueId] = useState(null); // For aggregated view
  
  // Detail modal state
  const [selectedIncentive, setSelectedIncentive] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [dealDetails, setDealDetails] = useState(null);
  const [demoDetails, setDemoDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Company Revenue Detail Modal
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [receivableModalOpen, setReceivableModalOpen] = useState(false);
  
  // Company Revenue Filters
  const [revenueMonthFilter, setRevenueMonthFilter] = useState('all');
  const [revenueEmployeeFilter, setRevenueEmployeeFilter] = useState('all');

  const isAdmin = currentUser?.role === 'ADMIN';
  const isManager = currentUser?.role === 'MANAGER' || currentUser?.role === 'SALES_MANAGER';
  const isAdminOrManager = isAdmin || isManager;

  // Redirect if user can't see incentives
  useEffect(() => {
    if (!settingsLoading && !canSeeIncentives()) {
      navigate('/dashboard');
    }
  }, [settingsLoading, canSeeIncentives, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [recordsRes, usersRes, teamConfigsRes, teamSqdSlabsRes] = await Promise.all([
          incentivesAPI.listRecords({ page_size: 200 }),
          isAdminOrManager ? usersAPI.list() : Promise.resolve({ data: [] }),
          incentivesAPI.listTeamConfigs().catch(() => ({ data: [] })),
          incentivesAPI.listTeamSQDSlabs().catch(() => ({ data: [] }))
        ]);
        const recordsData = recordsRes.data;
        const records = Array.isArray(recordsData) ? recordsData : (recordsData?.records || []);
        setIncentives(records);
        setUsers(usersRes.data || []);
        setTeamConfigs(teamConfigsRes.data || []);
        setTeamSqdSlabs(teamSqdSlabsRes.data || []);
        
        // Fetch Company Receivables if Admin
        if (isAdmin) {
            const [receivablesRes, statsRes] = await Promise.all([
                incentivesAPI.listReceivables({ page_size: 200 }).catch(() => ({ data: [] })),
                incentivesAPI.receivableStats().catch(() => ({ data: {} }))
            ]);
            setCompanyReceivables(receivablesRes.data || []);
            setReceivableStats(statsRes.data || {});
        }
        
        // Set default tab based on role
        if (!isAdmin) {
          setActiveTab('my-incentives');
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdminOrManager, isAdmin]);

  // Calculate totals
  const myIncentives = isAdmin ? [] : incentives.filter(inc => inc.user_id === currentUser?.id);
  const myTotalEarned = myIncentives.reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const myPendingApproval = myIncentives.filter(inc => inc.status === 'PENDING').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const myApproved = myIncentives.filter(inc => inc.status === 'APPROVED').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const myPayable = myIncentives.filter(inc => inc.status === 'PAYABLE').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const myPaid = myIncentives.filter(inc => inc.status === 'PAID').reduce((sum, inc) => sum + (inc.amount_paid || inc.amount_earned || 0), 0);

  const allTotalEarned = incentives.reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const allPendingApproval = incentives.filter(inc => inc.status === 'PENDING').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const allApproved = incentives.filter(inc => inc.status === 'APPROVED').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const allPayable = incentives.filter(inc => inc.status === 'PAYABLE').reduce((sum, inc) => sum + (inc.amount_earned || 0), 0);
  const allPaid = incentives.filter(inc => inc.status === 'PAID').reduce((sum, inc) => sum + (inc.amount_paid || inc.amount_earned || 0), 0);

  // Filter incentives
  const getFilteredIncentives = () => {
    let filtered = incentives;
    
    if (activeTab === 'my-incentives') {
      filtered = myIncentives;
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(inc => inc.metric_type === typeFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inc => inc.status === statusFilter);
    }
    if (userFilter !== 'all' && isAdminOrManager) {
      filtered = filtered.filter(inc => inc.user_id === userFilter);
    }
    
    return filtered;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  // Get applicable team conversion config for a month
  const getApplicableConfig = (periodMonth) => {
    const configs = teamConfigs
      .filter(c => c.effective_month <= periodMonth)
      .sort((a, b) => b.effective_month.localeCompare(a.effective_month));
    return configs[0] || null;
  };

  const getApplicableTeamSQDSlabMonth = (periodMonth) => {
    const slabs = (teamSqdSlabs || [])
      .filter(s => s.effective_month <= periodMonth)
      .sort((a, b) => b.effective_month.localeCompare(a.effective_month));
    return slabs[0]?.effective_month || null;
  };

  // View incentive details with calculation
  const handleViewDetails = async (incentive) => {
    setSelectedIncentive(incentive);
    setDetailModalOpen(true);
    setLoadingDetails(true);
    setDealDetails(null);
    setDemoDetails(null);
    
    try {
      // Fetch deal or demo details
      if (incentive.deal_id) {
        const dealRes = await dealsAPI.get(incentive.deal_id);
        setDealDetails(dealRes.data);
      }
      if (incentive.demo_id) {
        const demoRes = await demosAPI.get(incentive.demo_id);
        setDemoDetails(demoRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle approve/pay actions
  const handleApprove = async (id) => {
    try {
      await incentivesAPI.approveRecord(id);
      setIncentives(incentives.map(inc => inc.id === id ? { ...inc, status: 'APPROVED' } : inc));
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to approve',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleMarkPayable = async (id) => {
    try {
      await incentivesAPI.markPayable(id);
      setIncentives(incentives.map(inc => inc.id === id ? { ...inc, status: 'PAYABLE' } : inc));
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to mark payable',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await incentivesAPI.markPaid(id);
      setIncentives(incentives.map(inc => inc.id === id ? { ...inc, status: 'PAID', amount_paid: inc.amount_earned } : inc));
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to mark paid',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Prepare aggregated Conversion Revenue data
  const getConversionRevenue = () => {
    const conversionItems = companyReceivables.filter(r => 
        r.source_type === 'CONVERSION_SHARE' || r.source_type === 'ONE_TIME_CHARGE'
    );
    
    // Group by deal_id (or client_name if missing deal_id)
    const grouped = {};
    conversionItems.forEach(item => {
        const key = item.deal_id || item.client_name;
        if (!grouped[key]) {
            grouped[key] = {
                id: key,
                client_name: item.client_name,
                deal_id: item.deal_id,
                period_month: item.period_month, // Use the most recent or first one
                total_amount: 0,
                status: item.status, // Just take one status for now
                items: []
            };
        }
        grouped[key].total_amount += item.amount;
        grouped[key].items.push(item);
    });
    
    return Object.values(grouped);
  };

  const getSQDRevenue = () => {
    return companyReceivables.filter(r => r.source_type === 'SQD_REVENUE');
  };

  // View company receivable details
  const handleViewReceivableDetails = async (receivable) => {
    setSelectedReceivable(receivable);
    setReceivableModalOpen(true);
    setLoadingDetails(true);
    setDemoDetails(null);
    setDealDetails(null);
    
    try {
      // For SQD revenue, fetch demo details
      if (receivable.demo_id) {
        const demoRes = await demosAPI.get(receivable.demo_id);
        setDemoDetails(demoRes.data);
      }
      // For Conversion revenue, fetch deal details
      if (receivable.deal_id) {
        const dealRes = await dealsAPI.get(receivable.deal_id);
        setDealDetails(dealRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch receivable details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Build calculation breakdown for the modal
  const getCalculationBreakdown = () => {
    if (!selectedIncentive) return null;

    // ALWAYS use deal.mrr for current MRR (reflects WEF updates)
    // Line items are historical and may be stale
    const currentMRR = dealDetails?.mrr || 0;
    
    // Team conversion incentive % config should come from the deal snapshot (immutable based on win month)
    let dealSnapshot = null;
    if (dealDetails?.team_config_snapshot) {
      try {
        dealSnapshot = JSON.parse(dealDetails.team_config_snapshot);
      } catch {
        dealSnapshot = null;
      }
    }

    // Also parse team_incentive_config (already parsed by backend)
    const teamConfig = dealDetails?.team_incentive_config;

    // Fallback (only if snapshot missing): period-based config
    const periodConfig = getApplicableConfig(selectedIncentive.period_month);

    // Use teamConfig from deal if available (already parsed)
    const firstMonthPct = teamConfig?.['1M'] ?? dealSnapshot?.first_month_plan_percentage ?? periodConfig?.first_month_plan_percentage ?? 10;
    const setupPct = teamConfig?.['OTC'] ?? dealSnapshot?.setup_fee_percentage ?? periodConfig?.setup_fee_percentage ?? 10;
    const renewalPct = teamConfig?.['NextYr1M'] ?? dealSnapshot?.renewal_first_month_percentage ?? periodConfig?.renewal_first_month_percentage ?? 5;

    const configMonth = teamConfig?.wef_month || dealSnapshot?.effective_month || periodConfig?.effective_month || null;
    const configLabel = configMonth
      ? `Config Snapshot: ${new Date(`${configMonth}-01T00:00:00.000Z`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`
      : 'Config: (default)';

    // Extract setup fee from deal line items (historical, for one-time charges only)
    let setupFeeFromItems = 0;
    const oneTimeItems = [];

    if (dealDetails?.line_items) {
      for (const item of dealDetails.line_items) {
        const desc = (item.description || '').toLowerCase();
        const amount = item.total_price || item.unit_price || 0;

        if (desc.includes('setup') || desc.includes('one-time') || desc.includes('onetime') || desc.includes('one time')) {
          setupFeeFromItems += amount;
          oneTimeItems.push({ description: item.description, amount });
        }
      }
    }

    const setupFee = setupFeeFromItems;
    const hasPricingRevision = dealDetails?.pricing_revision_history && dealDetails.pricing_revision_history !== '[]';
    
    if (selectedIncentive.metric_type === 'SQD') {
      // Get config from demo snapshot if available
      const teamSqdConfig = demoDetails?.team_sqd_config;
      const sqd_position = demoDetails?.sqd_position_employee || teamSqdConfig?.employee_sqd_position;
      
      const slabMonth = teamSqdConfig?.wef_month || getApplicableTeamSQDSlabMonth(selectedIncentive.period_month);
      const slabLabel = slabMonth
        ? `Config Snapshot: ${new Date(`${slabMonth}-01T00:00:00.000Z`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`
        : 'Config: (not configured)';
      
      const components = [];
      
      // Show SQD position
      components.push({ label: 'SQD Position (this month)', value: sqd_position ? `#${sqd_position}` : 'N/A', bold: true, header: true });
      
      // Parse tier upgrade history if available
      let tierHistory = [];
      if (selectedIncentive.tier_upgrade_history) {
        try {
          tierHistory = JSON.parse(selectedIncentive.tier_upgrade_history) || [];
        } catch { tierHistory = []; }
      }
      
      // Show slabs from config snapshot
      const slabs = teamSqdConfig?.slabs || [];
      if (slabs.length > 0) {
        components.push({ label: '', value: '', divider: true });
        components.push({ label: 'Slab Configuration', value: '', bold: true, header: true });
        slabs.forEach((slab, idx) => {
          const maxLabel = slab.max ? slab.max : '∞';
          const isCurrent = sqd_position && sqd_position >= slab.min && (slab.max === null || sqd_position <= slab.max);
          components.push({ 
            label: `  Tier ${idx + 1}: ${slab.min}-${maxLabel} SQDs`, 
            value: formatMoney(slab.amount) + '/SQD',
            highlight: isCurrent,
            subItem: true
          });
        });
      }
      
      // Show tier upgrade breakdown if available
      if (tierHistory.length > 0) {
        components.push({ label: '', value: '', divider: true });
        components.push({ label: '📈 Tier Upgrades (Retrospective)', value: '', bold: true, header: true });
        
        let originalAmount = 0;
        let totalUpgrade = 0;
        tierHistory.forEach((upgrade, idx) => {
          if (idx === 0) {
            originalAmount = upgrade.from_amount || 0;
          }
          totalUpgrade += upgrade.diff || 0;
          components.push({ 
            label: `  SQD #${upgrade.sqd_count}: ₹${upgrade.from_amount} → ₹${upgrade.to_amount}`, 
            value: `+${formatMoney(upgrade.diff)}`,
            subItem: true,
            highlight: true
          });
        });
        
        components.push({ label: '', value: '', divider: true });
        components.push({ label: 'Original Tier Amount', value: formatMoney(originalAmount), bold: false });
        components.push({ label: 'Tier Upgrade Bonus', value: `+${formatMoney(totalUpgrade)}`, highlight: true });
      }
      
      components.push({ label: '', value: '', divider: true });
      components.push({ label: '→ Final Amount', value: formatMoney(selectedIncentive.amount_earned), isResult: true });

      return {
        type: 'SQD Incentive (Employee)',
        formula: tierHistory.length > 0 ? 'Original Tier + Tier Upgrade Bonuses' : 'Slab Rate based on monthly SQD count',
        components,
        total: selectedIncentive.amount_earned,
        expectedTotal: selectedIncentive.amount_earned,
        config: slabLabel,
        configSnapshot: teamSqdConfig
      };
    }
    
    if (selectedIncentive.metric_type === 'DEAL_CLOSED') {
      const recurringIncentive = (currentMRR * firstMonthPct) / 100;
      const setupIncentive = (setupFee * setupPct) / 100;
      const expectedTotal = recurringIncentive + setupIncentive;
      
      const components = [];
      
      // Show current MRR (the authoritative value after WEF updates)
      components.push({ label: 'Current Monthly Revenue (MRR)', value: formatMoney(currentMRR), bold: true, header: true });
      if (hasPricingRevision) {
        components.push({ label: '  ℹ️ Plan has been revised (WEF applied)', value: '', note: true });
      }
      components.push({ label: `1st Month Rate`, value: `${firstMonthPct}%`, highlight: true });
      components.push({ label: `→ Recurring Incentive`, value: formatMoney(recurringIncentive), isResult: true });
      components.push({ label: '', value: '', divider: true });
      
      // Show one-time items breakdown  
      components.push({ label: 'One-time Charges (Historical)', value: formatMoney(setupFee), bold: true, header: true });
      oneTimeItems.forEach(item => {
        components.push({ label: `  └ ${item.description}`, value: formatMoney(item.amount), subItem: true });
      });
      components.push({ label: `Setup Fee Rate`, value: `${setupPct}%`, highlight: true });
      components.push({ label: `→ Setup Incentive`, value: formatMoney(setupIncentive), isResult: true });
      components.push({ label: '', value: '', divider: true });
      components.push({ label: `Renewal Rate (Year 2+)`, value: `${renewalPct}%`, note: true });
      
      return {
        type: 'Deal Conversion Incentive (Employee)',
        formula: '(Current MRR × 1st Month %) + (One-time × Setup %)',
        components,
        total: selectedIncentive.amount_earned,
        expectedTotal: expectedTotal,
        config: configLabel,
        configSnapshot: teamConfig
      };
    }
    
    if (selectedIncentive.metric_type === 'RENEWAL') {
      const renewalIncentive = (currentMRR * renewalPct) / 100;
      
      const components = [];
      components.push({ label: 'Current Monthly Revenue (MRR)', value: formatMoney(currentMRR), bold: true, header: true });
      if (hasPricingRevision) {
        components.push({ label: '  ℹ️ Plan has been revised (WEF applied)', value: '', note: true });
      }
      components.push({ label: `Renewal Rate`, value: `${renewalPct}%`, highlight: true });
      components.push({ label: `→ Renewal Incentive`, value: formatMoney(renewalIncentive), isResult: true });
      
      return {
        type: 'Renewal Incentive (Employee)',
        formula: 'Current MRR × Renewal Rate %',
        components,
        total: selectedIncentive.amount_earned,
        expectedTotal: renewalIncentive,
        config: configLabel,
        configSnapshot: teamConfig
      };
    }
    
    return {
      type: selectedIncentive.metric_type,
      formula: 'Custom calculation',
      components: [
        { label: 'Amount Earned', value: formatMoney(selectedIncentive.amount_earned) }
      ],
      total: selectedIncentive.amount_earned
    };
  };

  // Show access denied for non-admin users when incentives are hidden
  if (!settingsLoading && !canSeeIncentives()) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <EyeOff className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
          Access Restricted
        </h2>
        <p className="text-slate-500 dark:text-slate-400 max-w-md">
          Incentive information is currently not available. Please contact your administrator for more details.
        </p>
        <Button 
          variant="outline" 
          className="mt-6"
          onClick={() => navigate('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  if (loading || settingsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading incentives...</span>
      </div>
    );
  }

  const SummaryCards = ({ total, pending, approved, payable, paid, isTeam = false }) => (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/30 dark:to-emerald-800/30 border-emerald-200 dark:border-emerald-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-emerald-600 dark:text-emerald-400">Total Earned</p>
              <p className="text-lg sm:text-2xl font-bold text-emerald-700 dark:text-emerald-300">{formatMoney(total)}</p>
            </div>
            <Award className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-500" />
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 border-amber-200 dark:border-amber-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-amber-600 dark:text-amber-400">Pending</p>
              <p className="text-lg sm:text-2xl font-bold text-amber-700 dark:text-amber-300">{formatMoney(pending)}</p>
            </div>
            <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-amber-500" />
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400">Approved</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-700 dark:text-blue-300">{formatMoney(approved)}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500" />
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 border-purple-200 dark:border-purple-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400">Payable</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-700 dark:text-purple-300">{formatMoney(payable)}</p>
            </div>
            <IndianRupee className="w-8 h-8 sm:w-10 sm:h-10 text-purple-500" />
          </div>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/30 dark:to-cyan-800/30 border-cyan-200 dark:border-cyan-800">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-cyan-600 dark:text-cyan-400">Paid</p>
              <p className="text-lg sm:text-2xl font-bold text-cyan-700 dark:text-cyan-300">{formatMoney(paid)}</p>
            </div>
            <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-500" />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const IncentiveTable = ({ data, showUser = false }) => (
    <>
      {/* Mobile Card View */}
      <div className="md:hidden space-y-3 p-3">
        {data.length === 0 ? (
          <div className="text-center py-10 text-slate-500">
            No incentives found
          </div>
        ) : (
          data.map((inc) => (
            <Card
              key={inc.id}
              className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 cursor-pointer"
              onClick={() => handleViewDetails(inc)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {inc.company_name}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {showUser && (
                        <Badge variant="outline" className="text-xs">
                          {inc.user_name || getUserName(inc.user_id)}
                        </Badge>
                      )}
                      <Badge
                        variant="outline"
                        className={
                          (inc.metric_type === 'SQD'
                            ? 'bg-blue-50 text-blue-700'
                            : inc.metric_type === 'RENEWAL'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-purple-50 text-purple-700') + ' text-xs'
                        }
                      >
                        {inc.metric_type}
                      </Badge>
                      <span className="text-xs text-slate-500">{inc.period_month}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-600 dark:text-emerald-400">
                      {formatMoney(inc.amount_earned)}
                    </p>
                    <Badge className={getStatusColor(inc.status)}>{inc.status}</Badge>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => handleViewDetails(inc)} title="View Details" className="w-full justify-between">
                    <span className="text-xs text-slate-500">View calculation</span>
                    <Eye className="w-4 h-4 text-slate-500" />
                  </Button>

                  {isAdminOrManager && inc.status === 'PENDING' && (
                    (() => {
                      const now = new Date();
                      const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                      const istMonthStr = `${istNow.getFullYear()}-${String(istNow.getMonth() + 1).padStart(2, '0')}`;
                      const disabled = inc.metric_type === 'SQD' && inc.period_month === istMonthStr;
                      const title = disabled ? `Cannot approve current month SQD (${istMonthStr})` : 'Approve';
                      return (
                        <Button variant="ghost" size="sm" onClick={() => !disabled && handleApprove(inc.id)} title={title} disabled={disabled}>
                          <CheckCircle2 className={"w-4 h-4 " + (disabled ? 'text-slate-300' : 'text-blue-500')} />
                        </Button>
                      );
                    })()
                  )}

                  {isAdminOrManager && inc.status === 'APPROVED' && (
                    <Button variant="ghost" size="sm" onClick={() => handleMarkPayable(inc.id)} title="Mark Payable">
                      <IndianRupee className="w-4 h-4 text-purple-500" />
                    </Button>
                  )}

                  {isAdmin && inc.status === 'PAYABLE' && (
                    <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(inc.id)} title="Mark Paid">
                      <TrendingUp className="w-4 h-4 text-emerald-500" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Table>
      <TableHeader>
        <TableRow>
          {showUser && <TableHead>Employee</TableHead>}
          <TableHead>Company</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Period</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.length > 0 ? data.map(inc => (
          <TableRow 
            key={inc.id} 
            className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900"
            onClick={() => handleViewDetails(inc)}
          >
            {showUser && <TableCell className="font-medium">{inc.user_name || getUserName(inc.user_id)}</TableCell>}
            <TableCell>{inc.company_name}</TableCell>
            <TableCell>
              <Badge variant="outline" className={inc.metric_type === 'SQD' ? 'bg-blue-50 text-blue-700' : inc.metric_type === 'RENEWAL' ? 'bg-emerald-50 text-emerald-700' : 'bg-purple-50 text-purple-700'}>
                {inc.metric_type}
              </Badge>
            </TableCell>
            <TableCell>{inc.period_month}</TableCell>
            <TableCell className="font-bold text-emerald-600 dark:text-emerald-400">
              <div className="flex flex-col">
                <span>{formatMoney(inc.amount_earned)}</span>
                {inc.metric_type === 'SQD' && inc.tier_upgrade_history && (() => {
                  try {
                    const history = JSON.parse(inc.tier_upgrade_history || '[]') || [];
                    if (!Array.isArray(history) || history.length === 0) return null;
                    const totalDiff = history.reduce((s, h) => s + (Number(h.diff) || 0), 0);
                    const first = history[0];
                    const last = history[history.length - 1];
                    const fromAmt = Number(first?.from_amount ?? 0);
                    const toAmt = Number(last?.to_amount ?? inc.amount_earned ?? 0);
                    return (
                      <span className="text-xs font-normal text-slate-500">
                        Tier upgrade(s): +{formatMoney(totalDiff)} (was {formatMoney(fromAmt)} → {formatMoney(toAmt)})
                      </span>
                    );
                  } catch (e) {
                    return null;
                  }
                })()}
              </div>
            </TableCell>
            <TableCell>
              <Badge className={getStatusColor(inc.status)}>{inc.status}</Badge>
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => handleViewDetails(inc)} title="View Details">
                  <Eye className="w-4 h-4 text-slate-500" />
                </Button>
                {isAdminOrManager && inc.status === 'PENDING' && (
                  (() => {
                    // Block SQD approvals in current calendar month (Asia/Kolkata)
                    const now = new Date();
                    const istNow = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
                    const istMonthStr = `${istNow.getFullYear()}-${String(istNow.getMonth() + 1).padStart(2, '0')}`;
                    const disabled = inc.metric_type === 'SQD' && inc.period_month === istMonthStr;
                    const title = disabled ? `Cannot approve current month SQD (${istMonthStr})` : 'Approve';
                    return (
                      <Button variant="ghost" size="sm" onClick={() => !disabled && handleApprove(inc.id)} title={title} disabled={disabled}>
                        <CheckCircle2 className={"w-4 h-4 " + (disabled ? 'text-slate-300' : 'text-blue-500')} />
                      </Button>
                    );
                  })()
                )}
                {isAdminOrManager && inc.status === 'APPROVED' && (
                  <Button variant="ghost" size="sm" onClick={() => handleMarkPayable(inc.id)} title="Mark Payable">
                    <IndianRupee className="w-4 h-4 text-purple-500" />
                  </Button>
                )}
                {isAdmin && inc.status === 'PAYABLE' && (
                  <Button variant="ghost" size="sm" onClick={() => handleMarkPaid(inc.id)} title="Mark Paid">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        )) : (
          <TableRow>
            <TableCell colSpan={showUser ? 7 : 6} className="text-center text-slate-500 py-8">
              No incentives found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
      </div>
    </>
  );

  // Get unique months and employees from receivables
  const revenueMonths = [...new Set(companyReceivables.map(r => r.period_month))].sort().reverse();
  const revenueEmployees = [...new Set(companyReceivables.map(r => r.employee_name).filter(Boolean))];

  // Filter receivables
  const filterReceivables = (data) => {
    return data.filter(rcv => {
      const matchesMonth = revenueMonthFilter === 'all' || rcv.period_month === revenueMonthFilter;
      const matchesEmployee = revenueEmployeeFilter === 'all' || rcv.employee_name === revenueEmployeeFilter;
      return matchesMonth && matchesEmployee;
    });
  };

  const SQDRevenueTable = ({ data }) => {
    const filteredData = filterReceivables(data);
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Period</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Employee</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredData.length > 0 ? filteredData.map(rcv => (
            <TableRow 
              key={rcv.id} 
              className="cursor-pointer hover:bg-slate-50"
              onClick={() => handleViewReceivableDetails(rcv)}
            >
              <TableCell className="font-medium">{rcv.period_month}</TableCell>
              <TableCell className="text-sm text-slate-500">
                {rcv.created_at ? new Date(rcv.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-'}
              </TableCell>
              <TableCell className="font-medium">{rcv.client_name || '-'}</TableCell>
              <TableCell className="text-sm">{rcv.employee_name || '-'}</TableCell>
              <TableCell className="font-bold text-emerald-600">{formatMoney(rcv.amount)}</TableCell>
              <TableCell>
                  <Badge className={getStatusColor(rcv.status)}>{rcv.status}</Badge>
              </TableCell>
            </TableRow>
          )) : (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                No SQD revenue records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  };

  const ConversionRevenueTable = ({ data }) => {
    // Apply filters to the grouped data
    const filteredData = data.filter(group => {
      const matchesMonth = revenueMonthFilter === 'all' || group.period_month === revenueMonthFilter;
      // Check if any item in the group matches the employee filter
      const matchesEmployee = revenueEmployeeFilter === 'all' || 
        group.items.some(item => item.employee_name === revenueEmployeeFilter);
      return matchesMonth && matchesEmployee;
    });
    
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead>Client</TableHead>
            <TableHead>Employee</TableHead>
            <TableHead>Period</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Total Amount</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredData.length > 0 ? filteredData.map(group => (
            <React.Fragment key={group.id}>
              <TableRow 
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedRevenueId(expandedRevenueId === group.id ? null : group.id)}
              >
                  <TableCell>
                      {expandedRevenueId === group.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </TableCell>
                  <TableCell className="font-medium">{group.client_name || 'Unknown'}</TableCell>
                  <TableCell className="text-sm">{group.items[0]?.employee_name || '-'}</TableCell>
                  <TableCell>{group.period_month}</TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {group.items[0]?.created_at ? new Date(group.items[0].created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '-'}
                  </TableCell>
                  <TableCell className="font-bold text-emerald-600">{formatMoney(group.total_amount)}</TableCell>
                  <TableCell>
                      <Badge className={getStatusColor(group.status)}>{group.status}</Badge>
                  </TableCell>
              </TableRow>
              {expandedRevenueId === group.id && (
                  <TableRow className="bg-slate-50">
                      <TableCell colSpan={7} className="p-4">
                          <div className="bg-white rounded-lg border p-4">
                              <h4 className="font-semibold mb-3 text-sm">Revenue Details for {group.client_name}</h4>
                              <Table>
                                  <TableHeader>
                                      <TableRow>
                                          <TableHead>Type</TableHead>
                                          <TableHead>Description</TableHead>
                                          <TableHead>Amount</TableHead>
                                      </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                      {group.items.map(item => (
                                          <TableRow 
                                            key={item.id}
                                            className="cursor-pointer hover:bg-slate-100"
                                            onClick={(e) => { e.stopPropagation(); handleViewReceivableDetails(item); }}
                                          >
                                              <TableCell>
                                                  <Badge variant="outline">{item.source_type?.replace('_', ' ')}</Badge>
                                              </TableCell>
                                              <TableCell className="text-xs">{item.description}</TableCell>
                                              <TableCell>{formatMoney(item.amount)}</TableCell>
                                          </TableRow>
                                      ))}
                                  </TableBody>
                              </Table>
                          </div>
                      </TableCell>
                  </TableRow>
              )}
            </React.Fragment>
          )) : (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                No conversion revenue records found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Incentives & Revenue</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            {isAdmin ? 'Manage team incentives and company revenue' : 'Track your earnings and payouts'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => window.location.href = '/admin/incentive-config'}>
            <Settings className="w-4 h-4 mr-2" />
            Configure Incentives
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {!isAdmin && <TabsTrigger value="my-incentives">My Incentives</TabsTrigger>}
          <TabsTrigger value="team-incentives">Team Incentives</TabsTrigger>
          {isAdmin && <TabsTrigger value="company-revenue">Company Revenue</TabsTrigger>}
        </TabsList>

        {/* My Incentives Tab */}
        {!isAdmin && (
          <TabsContent value="my-incentives" className="mt-6">
            <Tabs defaultValue="current" className="w-full">
                <TabsList className="w-full max-w-[400px] mb-4">
                    <TabsTrigger value="current">Current & Past</TabsTrigger>
                    <TabsTrigger value="upcoming">Upcoming Payouts</TabsTrigger>
                </TabsList>
                
                <TabsContent value="current">
                    <SummaryCards 
                      total={myTotalEarned} 
                      pending={myPendingApproval} 
                      approved={myApproved} 
                      payable={myPayable} 
                      paid={myPaid} 
                    />
                    
                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      {/* ... existing filters ... */}
                      <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          <SelectItem value="SQD">SQD</SelectItem>
                          <SelectItem value="DEAL_CLOSED">Deal Closed</SelectItem>
                          <SelectItem value="RENEWAL">Renewal</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="PENDING">Pending</SelectItem>
                          <SelectItem value="APPROVED">Approved</SelectItem>
                          <SelectItem value="PAYABLE">Payable</SelectItem>
                          <SelectItem value="PAID">Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                      <CardContent className="p-0">
                        <IncentiveTable data={getFilteredIncentives()} showUser={false} />
                      </CardContent>
                    </Card>
                </TabsContent>
                
                <TabsContent value="upcoming">
                    <UpcomingPayouts />
                </TabsContent>
            </Tabs>
          </TabsContent>
        )}

        {/* Team Incentives Tab */}
        <TabsContent value="team-incentives" className="mt-6">
          <SummaryCards 
            total={allTotalEarned} 
            pending={allPendingApproval} 
            approved={allApproved} 
            payable={allPayable} 
            paid={allPaid}
            isTeam={true}
          />

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="SQD">SQD</SelectItem>
                <SelectItem value="DEAL_CLOSED">Deal Closed</SelectItem>
                <SelectItem value="RENEWAL">Renewal</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="APPROVED">Approved</SelectItem>
                <SelectItem value="PAYABLE">Payable</SelectItem>
                <SelectItem value="PAID">Paid</SelectItem>
              </SelectContent>
            </Select>
            {isAdminOrManager && users.length > 0 && (
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Employee" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardContent className="p-0">
              <IncentiveTable data={getFilteredIncentives()} showUser={true} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Company Revenue Tab (Admin Only) */}
        {isAdmin && (
            <TabsContent value="company-revenue" className="mt-6">
                <Tabs defaultValue="current" className="w-full">
                    <TabsList className="w-full max-w-[400px] mb-4">
                        <TabsTrigger value="current">Invoiced / Due</TabsTrigger>
                        <TabsTrigger value="upcoming">Upcoming Invoices</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="current">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <Card className="bg-blue-50 border-blue-200">
                                <CardContent className="p-6">
                                    <p className="text-sm font-medium text-blue-600">Total Expected</p>
                                    <p className="text-2xl font-bold text-blue-700">{formatMoney(receivableStats.total_expected || 0)}</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-purple-50 border-purple-200">
                                <CardContent className="p-6">
                                    <p className="text-sm font-medium text-purple-600">Invoiced</p>
                                    <p className="text-2xl font-bold text-purple-700">{formatMoney(receivableStats.total_invoiced || 0)}</p>
                                </CardContent>
                            </Card>
                            <Card className="bg-emerald-50 border-emerald-200">
                                <CardContent className="p-6">
                                    <p className="text-sm font-medium text-emerald-600">Realized (Paid)</p>
                                    <p className="text-2xl font-bold text-emerald-700">{formatMoney(receivableStats.total_paid || 0)}</p>
                                </CardContent>
                            </Card>
                        </div>
                        
                        <div className="mb-4">
                          <div className="flex flex-wrap items-center gap-4 mb-4">
                            {/* Revenue Type Buttons */}
                            <div className="flex bg-slate-100 rounded-lg p-1">
                              <Button 
                                variant={revenueTab === 'all' ? 'default' : 'ghost'} 
                                size="sm"
                                onClick={() => setRevenueTab('all')}
                                className={revenueTab === 'all' ? 'bg-white shadow-sm' : ''}
                              >
                                All Revenue
                              </Button>
                              <Button 
                                variant={revenueTab === 'sqd' ? 'default' : 'ghost'} 
                                size="sm"
                                onClick={() => setRevenueTab('sqd')}
                                className={revenueTab === 'sqd' ? 'bg-orange-500 text-white shadow-sm' : ''}
                              >
                                <Target className="w-4 h-4 mr-1" />
                                SQD Only
                              </Button>
                              <Button 
                                variant={revenueTab === 'conversion' ? 'default' : 'ghost'} 
                                size="sm"
                                onClick={() => setRevenueTab('conversion')}
                                className={revenueTab === 'conversion' ? 'bg-emerald-500 text-white shadow-sm' : ''}
                              >
                                <TrendingUp className="w-4 h-4 mr-1" />
                                Conversion Only
                              </Button>
                            </div>

                            {/* Filters */}
                            <div className="flex gap-2 ml-auto">
                              <Select value={revenueMonthFilter} onValueChange={setRevenueMonthFilter}>
                                <SelectTrigger className="w-[130px]">
                                  <SelectValue placeholder="Month" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Months</SelectItem>
                                  {revenueMonths.map(month => (
                                    <SelectItem key={month} value={month}>{month}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select value={revenueEmployeeFilter} onValueChange={setRevenueEmployeeFilter}>
                                <SelectTrigger className="w-[150px]">
                                  <SelectValue placeholder="Employee" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Employees</SelectItem>
                                  {revenueEmployees.map(emp => (
                                    <SelectItem key={emp} value={emp}>{emp}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                            
                        <div className="mt-0">
                                <Card className="bg-white border-slate-200">
                                    <CardContent className="p-0">
                                        {revenueTab === 'sqd' ? (
                                            <SQDRevenueTable data={getSQDRevenue()} />
                                        ) : revenueTab === 'conversion' ? (
                                            <ConversionRevenueTable data={getConversionRevenue()} />
                                        ) : (
                                            <div className="space-y-8 p-4">
                                                <div>
                                                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                                        <Target className="w-5 h-5 text-blue-500" />
                                                        SQD Revenue
                                                    </h3>
                                                    <div className="border rounded-md">
                                                        <SQDRevenueTable data={getSQDRevenue()} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                                                        Conversion Revenue
                                                    </h3>
                                                    <div className="border rounded-md">
                                                        <ConversionRevenueTable data={getConversionRevenue()} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                    </TabsContent>
                    
                    <TabsContent value="upcoming">
                        <UpcomingInvoices />
                    </TabsContent>
                </Tabs>
            </TabsContent>
        )}
      </Tabs>

      {/* Detail Modal with Calculation */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="left-0 top-auto bottom-0 translate-x-0 translate-y-0 w-full max-w-none rounded-t-xl p-4 sm:p-6 max-h-[85vh] overflow-y-auto sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-w-[600px] sm:rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-500" />
              Incentive Calculation Details
            </DialogTitle>
            <DialogDescription>
              Detailed breakdown of how this incentive was calculated
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : selectedIncentive ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-500">Company</p>
                    <p className="font-medium">{selectedIncentive.company_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Employee</p>
                    <p className="font-medium">{selectedIncentive.user_name || getUserName(selectedIncentive.user_id)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Type</p>
                    <Badge variant="outline">{selectedIncentive.metric_type}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Period</p>
                    <p className="font-medium">{selectedIncentive.period_month}</p>
                  </div>
                </div>
              </div>

              {/* Calculation Breakdown */}
              {(() => {
                const breakdown = getCalculationBreakdown();
                if (!breakdown) return null;
                
                return (
                  <div className="space-y-3">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                      <p className="text-sm font-medium text-purple-700 dark:text-purple-300">{breakdown.type}</p>
                      <p className="text-xs text-purple-600 dark:text-purple-400">Formula: {breakdown.formula}</p>
                      {breakdown.config && (
                        <p className="text-xs text-purple-500 mt-1">{breakdown.config}</p>
                      )}
                    </div>
                    
                    {/* Config Snapshot Display - Different for SQD vs Deal types */}
                    {breakdown.configSnapshot && selectedIncentive?.metric_type !== 'SQD' && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2">📋 Incentive Config Used (Snapshot at Deal Win)</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-white dark:bg-slate-800 p-2 rounded">
                            <span className="text-blue-500">1st Month:</span>
                            <span className="ml-1 font-medium">{breakdown.configSnapshot['1M'] || 0}%</span>
                          </div>
                          <div className="bg-white dark:bg-slate-800 p-2 rounded">
                            <span className="text-blue-500">Setup Fee:</span>
                            <span className="ml-1 font-medium">{breakdown.configSnapshot['OTC'] || 0}%</span>
                          </div>
                          <div className="bg-white dark:bg-slate-800 p-2 rounded">
                            <span className="text-blue-500">Renewal:</span>
                            <span className="ml-1 font-medium">{breakdown.configSnapshot['NextYr1M'] || 0}%</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* SQD Config Snapshot Display */}
                    {breakdown.configSnapshot && selectedIncentive?.metric_type === 'SQD' && breakdown.configSnapshot.slabs && (
                      <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                        <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 mb-2">📋 SQD Slab Config (Snapshot at SQD Approval)</p>
                        <div className="space-y-1 text-xs">
                          {breakdown.configSnapshot.slabs.map((slab, idx) => (
                            <div key={idx} className="flex justify-between bg-white dark:bg-slate-800 p-2 rounded">
                              <span className="text-orange-600">Tier {idx + 1}: {slab.min}-{slab.max || '∞'} SQDs</span>
                              <span className="font-medium">₹{slab.amount?.toLocaleString()}/SQD</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="border rounded-lg divide-y dark:border-slate-700 dark:divide-slate-700">
                      {breakdown.components.map((comp, idx) => {
                        if (comp.divider) {
                          return <div key={idx} className="border-t border-slate-200 dark:border-slate-600" />;
                        }
                        return (
                          <div 
                            key={idx} 
                            className={cn(
                              "flex justify-between items-center p-3",
                              comp.isResult && "bg-emerald-50 dark:bg-emerald-900/30",
                              comp.highlight && "bg-blue-50 dark:bg-blue-900/30",
                              comp.note && "bg-slate-50 dark:bg-slate-900",
                              comp.header && "bg-slate-100 dark:bg-slate-800",
                              comp.subItem && "pl-6 py-1 text-xs bg-slate-50/50"
                            )}
                          >
                            <span className={cn(
                              "text-sm",
                              comp.isResult && "font-medium text-emerald-700 dark:text-emerald-400",
                              comp.note && "text-slate-500",
                              comp.header && "font-semibold",
                              comp.subItem && "text-slate-600 dark:text-slate-400"
                            )}>
                              {comp.label}
                            </span>
                            <span className={cn(
                              "font-medium",
                              comp.isResult && "text-emerald-600 dark:text-emerald-400",
                              comp.highlight && "text-blue-600 dark:text-blue-400",
                              comp.subItem && "text-slate-600 dark:text-slate-400 text-xs"
                            )}>
                              {comp.value}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="p-4 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-lg text-white">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Total Incentive Amount</span>
                        <span className="text-2xl font-bold">{formatMoney(breakdown.total)}</span>
                      </div>
                      {breakdown.expectedTotal && Math.abs(breakdown.expectedTotal - breakdown.total) > 1 && (
                        <p className="text-xs mt-1 text-emerald-100">
                          Expected: {formatMoney(breakdown.expectedTotal)} (diff may be due to rounding)
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Adjustment Note (if incentive was modified via WEF update) */}
              {selectedIncentive.adjustment_note && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">⚠️ Pricing Revision Applied</p>
                  <p className="text-xs text-amber-600 dark:text-amber-500">{selectedIncentive.adjustment_note}</p>
                </div>
              )}

              {/* Deal/Demo Details */}
              {dealDetails && (
                <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                  <p className="text-sm font-medium mb-2">Deal Details</p>
                  <div className="text-xs space-y-1">
                    <p><span className="text-slate-500">Deal ID:</span> {dealDetails.id}</p>
                    <p><span className="text-slate-500">Total Value:</span> {formatMoney(dealDetails.total_amount)}</p>
                    <p><span className="text-slate-500">Current MRR:</span> {formatMoney(dealDetails.mrr)}</p>
                    {dealDetails.line_items?.map((item, i) => (
                      <p key={i}><span className="text-slate-500">{item.description}:</span> {formatMoney(item.total_price)}</p>
                    ))}
                  </div>
                  
                  {/* Pricing Revision History */}
                  {dealDetails.pricing_revision_history && (() => {
                    try {
                      const history = JSON.parse(dealDetails.pricing_revision_history);
                      if (history && history.length > 0) {
                        return (
                          <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Pricing History</p>
                            {history.map((rev, idx) => (
                              <div key={idx} className="text-xs p-2 bg-white dark:bg-slate-800 rounded mb-1 border">
                                <div className="flex justify-between">
                                  <span className="text-slate-500">
                                    {new Date(rev.at).toLocaleDateString('en-IN')}
                                  </span>
                                  <span className="text-blue-600 font-medium">
                                    WEF: {new Date(rev.wef).toLocaleDateString('en-IN')}
                                  </span>
                                </div>
                                <div className="mt-1">
                                  <span className="text-slate-400">Old: </span>
                                  <span className="line-through text-red-500">{formatMoney(rev.old_values?.plan_amount)}</span>
                                  <span className="text-slate-400 mx-1">→</span>
                                  <span className="text-emerald-600 font-medium">{formatMoney(rev.changes_summary?.plan_amount)}</span>
                                </div>
                                <p className="text-slate-500 mt-1 italic">&quot;{rev.reason}&quot;</p>
                              </div>
                            ))}
                          </div>
                        );
                      }
                    } catch (e) {
                      return null;
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* Status */}
              <div className="flex justify-between items-center pt-3 border-t dark:border-slate-700">
                <span className="text-sm text-slate-500">Current Status</span>
                <Badge className={getStatusColor(selectedIncentive.status)}>{selectedIncentive.status}</Badge>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Company Revenue Detail Modal */}
      <Dialog open={receivableModalOpen} onOpenChange={setReceivableModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-emerald-500" />
              Company Revenue Details
            </DialogTitle>
            <DialogDescription>
              Breakdown of revenue calculation for this record
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">Loading details...</span>
            </div>
          ) : selectedReceivable ? (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Client</p>
                  <p className="font-semibold">{selectedReceivable.client_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Employee</p>
                  <p className="font-medium">{selectedReceivable.employee_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Type</p>
                  <Badge variant="outline">{selectedReceivable.source_type?.replace('_', ' ')}</Badge>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Period</p>
                  <p className="font-medium">{selectedReceivable.period_month}</p>
                </div>
              </div>

              {/* Amount Breakdown */}
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-700 dark:text-emerald-400 font-medium">Revenue Amount</span>
                  <span className="text-2xl font-bold text-emerald-600">{formatMoney(selectedReceivable.amount)}</span>
                </div>
              </div>

              {/* SQD Config Details */}
              {selectedReceivable.source_type === 'SQD_REVENUE' && demoDetails?.company_sqd_config && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-2">📋 SQD Slab Config (Snapshot)</p>
                  <p className="text-xs text-blue-600 mb-2">WEF: {demoDetails.company_sqd_config.wef_month}</p>
                  <div className="space-y-1">
                    {demoDetails.company_sqd_config.slabs?.map((slab, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-white dark:bg-slate-800 p-2 rounded">
                        <span>Tier {idx + 1}: {slab.min}-{slab.max || '∞'} SQDs</span>
                        <span className="font-medium">₹{slab.amount?.toLocaleString()}/SQD</span>
                      </div>
                    ))}
                  </div>
                  {demoDetails.sqd_position_company && (
                    <p className="mt-2 text-xs text-blue-600">Company SQD Position: #{demoDetails.sqd_position_company}</p>
                  )}
                </div>
              )}

              {/* Deal Details for Conversion */}
              {(selectedReceivable.source_type === 'CONVERSION_SHARE' || selectedReceivable.source_type === 'ONE_TIME_CHARGE') && dealDetails && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200">
                  <p className="text-sm font-semibold text-purple-700 mb-2">Deal Details</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-purple-600">Deal ID</p>
                      <p className="font-mono text-xs">{dealDetails.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600">MRR</p>
                      <p className="font-medium">{formatMoney(dealDetails.mrr)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600">Total Value</p>
                      <p className="font-medium">{formatMoney(dealDetails.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600">Plan</p>
                      <p className="font-medium">{dealDetails.plan_name || 'Custom'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedReceivable.description && (
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded">
                  <p className="text-xs text-slate-500 mb-1">Description</p>
                  <p className="text-sm">{selectedReceivable.description}</p>
                </div>
              )}

              {/* Status */}
              <div className="flex justify-between items-center pt-3 border-t">
                <span className="text-sm text-slate-500">Status</span>
                <Badge className={getStatusColor(selectedReceivable.status)}>{selectedReceivable.status}</Badge>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IncentivesPage;
